/**
 * A single selectable option in a scenario, as an accessible radio tile (keyboard-operable; the
 * hidden radio's focus is mirrored to the tile via has-[:focus-visible]).
 *
 * Options are shuffled per run (see lib/shuffle.ts), so the tile shows a DISPLAY letter by position
 * (`displayLabel`), not the source label — letters never leak the underlying order.
 *
 * Modes:
 *   - "select"        deciding — neutral; highlights when selected; tier hidden. When selected, an
 *                     inline "Commit" button appears on the tile so the player never scrolls to commit.
 *   - "chosen"        after commit — the picked option; reveals tier + the per-metric score change +
 *                     reaction + principle, tinted, "Your choice".
 *   - "revealed"      after commit (untimed practice) — a NOT-picked option whose tier + score + reaction
 *                     are also shown (learn the full spread of consequences).
 *   - "revealed-tier" after commit (timed) — a not-picked option showing ONLY its quality tier (so the
 *                     player still learns which options were good/bad), with text dimmed and prose withheld.
 */
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { DIMENSION_IDS, type Choice, type DimensionId } from "@patc/content-schema";
import { TierBadge } from "./TierBadge";
import { TIER_STYLE, cx } from "../lib/ui";

export type OptionMode = "select" | "chosen" | "revealed" | "revealed-tier";

const SHORT_DIM: Record<DimensionId, string> = {
  dealSecurity: "Deal security",
  clientSatisfaction: "Client",
  counterpartyCooperation: "Counterparty",
  transactionProcess: "Process",
};

/** The four per-metric point changes for this option — the exact +/- the player just earned. */
function ScoreDeltaPills({ score }: { score: Choice["score"] }) {
  return (
    <dl className="mt-1 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
      {DIMENSION_IDS.map((d) => {
        const v = score[d] ?? 0;
        const Icon = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
        const tone = v > 0 ? "text-[var(--success)]" : v < 0 ? "text-[var(--danger)]" : "text-ink-500";
        return (
          <div key={d} className="flex items-center justify-between gap-1.5 rounded-sm bg-panel px-2 py-1">
            <dt className="truncate text-xs text-ink-600">{SHORT_DIM[d]}</dt>
            <dd className={cx("flex shrink-0 items-center gap-0.5 font-mono text-xs font-semibold tabular-nums", tone)}>
              <Icon size={13} strokeWidth={2.5} aria-hidden="true" />
              {v > 0 ? "+" : ""}
              {v}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function OptionTile({
  choice,
  mode,
  selected,
  displayLabel,
  onSelect,
  onCommit,
}: {
  choice: Choice;
  mode: OptionMode;
  selected: boolean;
  /** Letter shown on the tile by DISPLAY position (options shuffle per run); defaults to the source label. */
  displayLabel?: string;
  onSelect: () => void;
  /** When provided and this tile is the selected one (select mode), shows the inline Commit button. */
  onCommit?: () => void;
}) {
  const tier = TIER_STYLE[choice.tier];
  const showReaction = mode === "chosen" || mode === "revealed";
  const showTier = mode === "chosen" || mode === "revealed" || mode === "revealed-tier";
  const dimmed = mode === "revealed-tier";
  return (
    <label
      className={cx(
        "group relative flex gap-3 rounded-sm border p-4 transition-colors has-[:focus-visible]:shadow-[var(--shadow-focus)]",
        mode === "select" &&
          (selected
            ? "cursor-pointer border-ink bg-ink-100"
            : "cursor-pointer border-ink-200 bg-card hover:border-ink-400"),
        mode === "chosen" && cx(tier.border, tier.bgTint),
        mode === "revealed" && cx(tier.border, "bg-card"),
        // Not-chosen in timed reveal: de-emphasised via the lighter border + no tint, NOT opacity —
        // dimming the prose under the warm card would drop it below WCAG AA contrast.
        dimmed && "border-ink-150 bg-card",
      )}
    >
      <input
        type="radio"
        name="option"
        className="sr-only"
        checked={selected}
        disabled={mode !== "select"}
        onChange={onSelect}
      />
      <span
        className={cx(
          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border font-mono text-sm",
          selected || showReaction ? "border-ink text-ink" : "border-ink-300 text-ink-500",
        )}
        aria-hidden="true"
      >
        {displayLabel ?? choice.label}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className={cx("text-fluid-base leading-relaxed", dimmed ? "text-ink-600" : "text-ink-800")}>
          {choice.text}
        </p>

        {showTier && (
          <div className="flex flex-wrap items-center gap-2">
            <TierBadge tier={choice.tier} />
            {mode === "chosen" && <span className="text-xs font-medium text-ink-500">· Your choice</span>}
          </div>
        )}

        {showReaction && (
          <div className="mt-1 flex flex-col gap-2">
            <div>
              <p className="t-overline mb-1 text-ink-500">Points this decision earned</p>
              <ScoreDeltaPills score={choice.score} />
            </div>
            <p className="text-sm leading-relaxed text-ink-600">
              <span className="font-semibold text-ink-700">Reaction. </span>
              {choice.reaction}
            </p>
            {choice.principle && (
              <p className="text-sm leading-relaxed text-ink-700">
                <span className="font-semibold">Principle. </span>
                {choice.principle}
              </p>
            )}
          </div>
        )}

        {mode === "select" && selected && onCommit && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onCommit();
            }}
            className="mt-1 inline-flex items-center gap-2 self-start rounded-sm bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-hover"
          >
            Commit this decision <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
    </label>
  );
}
